import * as Msal from "@azure/msal-browser";

import * as HelperSrc from "../HelperSrc";
import { request } from "../request";
import { MsalResponse, MsalToken, RequestObject } from "../interface";
import { store } from "../store";
import { store as uiStore } from "../store/ui";
import { alertBox } from "../actions/alertBox";
import { loginAd } from "../actions/login";
import { clearSession } from "../actions/clearSession";

let msalConfig: Msal.Configuration;
let myMSALObj: Msal.PublicClientApplication;
let rq: Msal.RedirectRequest;
let silentRq: Msal.SilentRequest;

export async function silentRenew(curr: MsalToken): Promise<MsalToken | null> {
    try {
        await new Promise((resolve) => {
            function msalCheck() {
                if (myMSALObj) {
                    resolve("ok");
                } else {
                    setTimeout(msalCheck, 500);
                }
            }

            msalCheck();
        });

        const account = myMSALObj.getAccountByHomeId(curr.homeAccountId);

        silentRq = {
            account: account || undefined,
            scopes: [...rq.scopes]
        };

        const result: Msal.AuthenticationResult = await myMSALObj.acquireTokenSilent(silentRq);

        if (result) {
            if (result.accessToken !== curr.accessToken) {
                curr.accessToken = result.accessToken;
                curr.homeAccountId = result?.account?.homeAccountId || "";
                curr.lastUpdate = Date.now();
                localStorage.setItem(`${HelperSrc.LABEL}_ad_token`, JSON.stringify(curr));
            }
            return curr;
        }
    } catch (e) {
        if (e instanceof Error) {
            HelperSrc.writeLog("adLogin.tsx -> silentRenew()", e.message);
        }
    }
    return null;
}

export function updateLastPage(): void {
    let pathname = location.pathname;

    if (uiStore && uiStore.basePath && uiStore.basePath !== "/" && location.pathname.startsWith(uiStore.basePath)) {
        pathname = pathname.substring(uiStore.basePath.length);
    }

    localStorage.setItem(`${HelperSrc.LABEL}_lastpage`, pathname);
}

export function adResetStatus(): void {
    uiStore.setLoaded(true);
    uiStore.setActiveDirectoryReady(false);
    store.setIsAD(false);
    localStorage.removeItem(`${HelperSrc.LABEL}_ad_token`);
    localStorage.removeItem(`${HelperSrc.LABEL}_ad_init_status`);
    localStorage.removeItem(`${HelperSrc.LABEL}_ad_working_status`);
}

const adInit = async (): Promise<void> => {
    const resp = await request<RequestObject<MsalResponse>, null>({
        path: "/activedir/",
        method: "GET",
        noAuth: true
    });

    const data = ((resp as { data?: MsalResponse })?.data || (resp as MsalResponse)) as MsalResponse;

    if (!data || !data.adclient || !data.adtenant) {
        adResetStatus();

        alertBox("Cannot connect server").catch(() => {
            store.doNotice("An unexpected error occurred");
        });

        return;
    }

    rq = {
        scopes: [`${data.adclient || ""}/.default`, "email"]
    };
    msalConfig = {
        auth: {
            clientId: `${data.adclient || ""}`,
            authority: `${HelperSrc.URL_AD_LOGIN}/${data.adtenant || ""}`
        },
        cache: {
            cacheLocation: "localStorage"
        }
    };

    myMSALObj = new Msal.PublicClientApplication(msalConfig);

    await myMSALObj.initialize();

    myMSALObj
        .handleRedirectPromise()
        .then(authCallback)
        .catch(() => {
            adResetStatus();
            alertBox("AD Error").catch(() => {
                store.doNotice("An unexpected error occurred");
            });
        });

    new Promise((resolve) => {
        function msalAuthCheck() {
            const adTokenStr = localStorage.getItem(`${HelperSrc.LABEL}_ad_token`);
            const token = adTokenStr ? (JSON.parse(adTokenStr) as MsalToken) : null;

            if (token) {
                resolve("ok");
            } else {
                setTimeout(msalAuthCheck, 500);
            }
        }
        msalAuthCheck();
    }).catch(() => {
        adResetStatus();

        store.doNotice("An unexpected error occurred");
    });

    uiStore.setActiveDirectoryReady(true);

    // ADLogin Renew
    setInterval(() => {
        let adToken: MsalToken | null;
        try {
            const adTokenStr = localStorage.getItem(`${HelperSrc.LABEL}_ad_token`);
            adToken = adTokenStr ? (JSON.parse(adTokenStr) as MsalToken) : null;

            if (adToken && Date.now() - adToken.lastUpdate > 60000 * 10) {
                silentRenew(adToken)
                    .then((token) => {
                        token = token || adToken;
                        if (token) {
                            token.lastUpdate = Date.now();
                            localStorage.setItem(`${HelperSrc.LABEL}_ad_token`, JSON.stringify(token));
                        }
                    })
                    .catch(() => {
                        adResetStatus();
                    });
            }
        } catch (e) {
            if (e instanceof Error) {
                HelperSrc.writeLog("adLogin.tsx -> setInterval()", e.message);
            }

            adResetStatus();

            return;
        }
    }, 10000);
};

function authCallback(resp: Msal.AuthenticationResult | null) {
    if (resp) {
        localStorage.setItem(`${HelperSrc.LABEL}_ad_token`, JSON.stringify(resp));
        loginAd(resp.accessToken);
    }
}

function adLogin(): Promise<void> {
    return myMSALObj.loginRedirect(rq);
}

function adLogout(): void {
    if (myMSALObj) {
        const logOutRequest: Msal.EndSessionRequest = {
            postLogoutRedirectUri: `${HelperSrc.URL_AD_REDIRECT}`,
            onRedirectNavigate: () => {
                clearSession();

                return true;
            }
        };

        myMSALObj.logoutRedirect(logOutRequest).catch(() => {
            //
        });
    }
}

export { adLogin, adLogout, adInit };
